import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

const STARTER_FEATURES = [
  'basic_scheduling', 'calendar', 'conflict_detection',
  'contextual_standings', 'push_notifications', 'league_sync', 'manual_matches'
];

const FAMILY_FEATURES = [
  ...STARTER_FEATURES,
  'travel_aware_conflicts', 'maps_directions', 'departure_calculator', 'weather',
  'email_digest', 'full_standings', 'position_analysis', 'critical_matches',
  'power_rankings', 'scenarios'
];

const TIER_FEATURES: Record<string, string[]> = {
  STARTER: STARTER_FEATURES,
  FAMILY: FAMILY_FEATURES,
};

// System-wide maximums — same for all tiers
const SYSTEM_LIMITS = { maxPlayers: 10, maxLeagues: 10 };

export const useSubscriptionTier = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const currentOrgId = user?.currentOrganizationId;
  const currentOrg = user?.availableOrganizations?.find(o => o.organizationId === currentOrgId);
  const tier = currentOrg?.subscriptionTier || 'STARTER';

  const hasFeature = (feature: string): boolean => {
    const features = TIER_FEATURES[tier] || TIER_FEATURES.STARTER;
    return features.includes(feature);
  };

  const unlimitedQuota = currentOrg?.unlimitedQuota || false;
  const limits = unlimitedQuota
    ? { maxPlayers: -1, maxLeagues: -1 }
    : SYSTEM_LIMITS;

  const isAtLimit = (resourceType: 'players' | 'leagues', currentCount: number): boolean => {
    if (unlimitedQuota) return false;
    const limit = resourceType === 'players' ? limits.maxPlayers : limits.maxLeagues;
    if (limit === -1) return false;
    return currentCount >= limit;
  };

  return {
    tier,
    hasFeature,
    isAtLimit,
    limits,
    isStarter: tier === 'STARTER',
    isFamily: tier === 'FAMILY',
  };
};
